import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";

import { addHotkeyToRegistry } from "@btnet/core-components/Hotkey/hotkey.registry";

import type { DialogTrigger } from "./searchDialog.learn";

const SEARCH_HOTKEY = { command: "ctrl+k, cmd+k", friendlyName: "Search" };

/**
 * The presses hotkeys-js matched for "ctrl+k, cmd+k": K plus ctrl or cmd, nothing else
 * held. `code` stands in for `key` on layouts whose K key types a non-Latin letter.
 */
function isSearchHotkey(event: KeyboardEvent) {
    const isK = /^[a-z]$/i.test(event.key)
        ? event.key.toLowerCase() === "k"
        : event.code === "KeyK";
    return isK && event.ctrlKey !== event.metaKey && !event.altKey && !event.shiftKey;
}

/**
 * Open/close state, plus the two things a centered modal owes the page that an
 * anchored dropdown does not: focus returns to whatever opened it, and the page
 * behind the scrim stops scrolling.
 *
 * cmd/ctrl+K rather than the registry's `HotkeyCommands.search`, which is
 * ctrl+space. The key is swallowed, so the browser does not claim it.
 *
 * Reports which trigger opened it, for telemetry.
 */
export function useSearchDialogOpen() {
    const [isOpen, setIsOpen] = useState(false);
    const [trigger, setTrigger] = useState<DialogTrigger>("icon");
    const triggerRef = useRef<HTMLElement | null>(null);

    const open = useCallback(() => {
        setTrigger("icon");
        setIsOpen(true);
    }, []);
    const close = useCallback(() => setIsOpen(false), []);

    // Not `useHotkey`: react-hotkeys-hook shares one hotkeys-js table, and a core
    // BTInput or BTCheckbox without a `hotkey` binds an empty key whose cleanup clears
    // that whole table when a modal opens or the route changes. This listener survives it.
    useEffect(() => {
        const onKeyDown = (event: KeyboardEvent) => {
            if (!isSearchHotkey(event)) {
                return;
            }
            event.preventDefault();
            event.stopPropagation();
            if (event.repeat) {
                return;
            }
            setTrigger("hotkey");
            setIsOpen(true);
        };
        document.addEventListener("keydown", onKeyDown);
        const unregister = addHotkeyToRegistry(SEARCH_HOTKEY);
        return () => {
            document.removeEventListener("keydown", onKeyDown);
            unregister();
        };
    }, []);

    // A layout effect, so this reads activeElement before the dialog mounts
    // and takes focus off the trigger.
    useLayoutEffect(() => {
        if (!isOpen) {
            return;
        }
        triggerRef.current = document.activeElement as HTMLElement | null;
    }, [isOpen]);

    useEffect(() => {
        if (isOpen) {
            return;
        }
        triggerRef.current?.focus();
        triggerRef.current = null;
    }, [isOpen]);

    // Also on unmount: a route change tears the dialog down while still open,
    // and the closing-edge effect above never runs.
    useEffect(() => {
        return () => {
            triggerRef.current?.focus();
            triggerRef.current = null;
        };
    }, []);

    useEffect(() => {
        if (!isOpen) {
            return;
        }
        const { body } = document;
        const previousOverflow = body.style.overflow;
        body.style.overflow = "hidden";
        return () => {
            body.style.overflow = previousOverflow;
        };
    }, [isOpen]);

    return { isOpen, open, close, trigger };
}
